import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useAtom } from 'jotai';
import {
  base_api_url,
  default_api_headers,
  post_api_headers,
} from '../utils/Store';
import { toast } from 'sonner';
import { useMsal, AuthenticatedTemplate } from '@azure/msal-react';
import { useLocation } from 'react-router-dom';
import { X, ChevronDown, Search, Loader2, Plus, Trash2 } from 'lucide-react';
import { UserContext } from './UserContext';

const getRoleBadgeStyle = (role) => {
  switch (role?.toLowerCase()) {
    case 'owner':
      return 'bg-blue-100 text-blue-800';
    case 'editor':
      return 'bg-green-100 text-green-800';
    case 'viewer':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

const CreateProjectPopup = ({ onClose, editingProject }) => {
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const location = useLocation();
  const username = activeAccount?.name;
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [selectedWorkflow, setSelectedWorkflow] = useState(
    location.pathname.split('/')[1],
  );
  const [ownerTransfer, setOwnerTransfer] = useState('');
  const [nameError, setNameError] = useState(null);
  const [descriptionError, setDescriptionError] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const postapi_header = useAtom(post_api_headers)[0];
  const [activeTab, setActiveTab] = useState('LBDD');
  const idToken = sessionStorage.getItem('id_token');
  const { userProfile } = useContext(UserContext);
  const companyId = userProfile?.companyId;
  // New state for collaborators
  const [isCollaboratorsOpen, setIsCollaboratorsOpen] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  const [currentCollaborators, setCurrentCollaborators] = useState([]);
  const [selectedCollaborators, setSelectedCollaborators] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const apiUrl = process.env.REACT_APP_API_PROJECT_MANAGEMENT_API;
  const defaultheader = useAtom(default_api_headers)[0];

  

  const availableWorkflows = () => {
    if (selectedWorkflow === 'retrosynthesis') {
      return <option value="retrosynthesis">Retrosynthesis</option>;
    }
    if (selectedWorkflow === 'admet') {
      return <option value="admet">ADMET</option>;
    }
    if (selectedWorkflow === 'generativeai') {
      return <option value="generativeai">Generative AI</option>;
    }
    if (selectedWorkflow === 'virtualscreening') {
      return <option value="virtualscreening">virtual screening</option>;
    }
     if (selectedWorkflow === 'docking') {
      return <option value="docking">Docking</option>;
    }
     if (selectedWorkflow === 'boltz') {
      return <option value="boltz">Boltz</option>;
    }
    return null;
  };

  // Function to fetch current collaborators
  const fetchCurrentCollaborators = async (projectId) => {
    if (!projectId) return;
    const idToken = sessionStorage.getItem("id_token");
    setLoading(true);
    try {
      const response = await axios.get(
        `${apiUrl}/api/projects/get-collaborators`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            // ...defaultheader,
          },
          params: { projectId },
        },
      );
      setCurrentCollaborators(response.data);
    } catch (error) {
      // console.error('Failed to fetch collaborators:', error);
      // toast.error('Failed to fetch collaborators');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (editingProject) {
      setProjectName(editingProject.projectName);
      setProjectDescription(editingProject.projectDescription);
      setSelectedWorkflow(editingProject.workflow);
      setOwnerTransfer(editingProject.ownerTransfer);
      fetchCurrentCollaborators(editingProject.id);
    } else {
      setProjectName('');
      setProjectDescription('');
      setSelectedWorkflow(location.pathname.split('/')[1]);
      setOwnerTransfer('');
      setCurrentCollaborators([]);
    }
  }, [editingProject, location.pathname]);

  // Function to search collaborators
  const searchCollaborators = async (value) => {
    setSearchTerm(value);
    if (!value.trim()) {
      setCollaborators([]);
      return;
    }
    const idToken = sessionStorage.getItem("id_token");
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/api/projects/search`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          ...postapi_header,
        },
        params: {
          companyId: companyId,
          searchTerm: value,
        },
        validateStatus: function (status) {
          return status >= 200 && status < 400; // Accept redirects
        },
        maxRedirects: 5,
        followRedirects: true,
      });

      // console.log('API Response:', response.data); // Log the response

      const allUsers = [];
      response.data.forEach((item) => {
        // Ensure you’re processing the response correctly
        if (item.id) {
          allUsers.push({
            id: item.id,
            firstName: item.firstName,
            lastName: item.lastName,
            userEmail: item.userEmail,
          });
        }
      });

      const uniqueUsers = allUsers.filter(
        (user, index, self) =>
          index === self.findIndex((u) => u.id === user.id) &&
          !selectedCollaborators.some((selected) => selected.id === user.id),
      );

      // console.log('Unique Users:', uniqueUsers); // Log the processed users

      setCollaborators(uniqueUsers); // Set collaborators state
    } catch (error) {
      // console.error('Search error details:', {
      //   message: error.message,
      //   response: error.response,
      //   status: error.response?.status
      // });
      toast.error('Failed to search collaborators');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCollaborator = (collaborator) => {
    setSelectedCollaborators([...selectedCollaborators, collaborator]); // Add to selected collaborators
    setCollaborators(collaborators.filter((c) => c.id !== collaborator.id)); // Remove from search results
    setSearchTerm(''); // Clear search term
  };

  const handleRemoveCollaborator = (collaborator) => {
    setSelectedCollaborators(
      selectedCollaborators.filter((c) => c.id !== collaborator.id),
    );
  };

  const handleSave = async () => {
    const productId =
      selectedWorkflow === 'retrosynthesis'
        ? 1
        : selectedWorkflow === 'admet'
          ? 2
          : selectedWorkflow === 'generativeai'
            ? 3
            : selectedWorkflow === 'virtualscreening'
              ? 4
              : selectedWorkflow === 'docking'
                ? 5
              : selectedWorkflow === 'boltz'
                ? 6
                : null;
    try {
      // First create/update the project
      const projectResponse = await axios({
        method: editingProject ? 'PUT' : 'POST',
        url: editingProject
          ? `${apiUrl}/api/projects?id=${encodeURIComponent(editingProject.id)}`
          : `${apiUrl}/api/projects`,
        headers: {
          Authorization: `Bearer ${idToken}`,
          ...postapi_header,
        },
        data: {
          createdBy: username,
          createdDate: new Date().toISOString(),
          updatedBy: username,
          updatedDate: new Date().toISOString(),
          projectName,
          projectDescription,
          workflow: selectedWorkflow,
          ownerTransfer: ownerTransfer,
          projectResult: 'In Progress',
          projectBlobStoragePath: 'string',
          companyId: companyId,
          productId,
          active: true,
        },
      });
  
      if (projectResponse.status === 200 || projectResponse.status === 201) {
        // Add collaborators if any are selected
        const projectId = projectResponse.data.id;
  
        for (const collaborator of selectedCollaborators) {
          await axios.post(`${apiUrl}/api/projects/add-collaborator`, null, {
            headers: {
              Authorization: `Bearer ${idToken}`,
              // ...defaultheader,
            },
            params: {
              userId: collaborator.id,
              projectId,
              productId,
            },
          });
        }

        toast.success(
          `Project ${editingProject ? 'updated' : 'created'} successfully`,
        );
        onClose(projectResponse.data);
      }
    } catch (error) {
      // console.error('Error saving project:', error);

      if (error.response) {
        const { status, data } = error.response;

        if (data?.errorMessage) {
          toast.error(data.errorMessage);
          return;
        }

        // Check for Access Denied error
        if (status === 500 && data.errorMessage === 'Access Denied') {
          // Display different message based on whether creating or editing
          if (editingProject) {
            toast.info(
              'Access Denied: You do not have permission to edit a project.',
            );
          } else {
            toast.info(
              'Access Denied: You do not have permission to create a project.',
            );
          }
          onClose();
        } else {
          toast.error('Failed to save project');
        }
      } else {
        toast.error('Failed to save project');
      }
    }
  };

  const handleDeleteCollaborator = async (userId) => {
    const idToken = sessionStorage.getItem("id_token");
    try {
      setDeletingUserId(userId);
      await axios.delete(`${apiUrl}/api/projects/delete-collaborator`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          // ...defaultheader,
        },
        params: {
          userId,
          projectId: editingProject.id,
          productId: editingProject.productId,
        },
      });

      toast.success('Collaborator removed successfully');
      await fetchCurrentCollaborators(editingProject.id);
    } catch (error) {
      // console.error('Delete error:', error);
      toast.error('Failed to remove collaborator');
    } finally {
      setDeletingUserId(null);
    }
  };
  const isFormValid =
    projectName.trim() !== '' && projectDescription.trim() !== '';

  useEffect(() => {
    if (editingProject) {
      setProjectName(editingProject.projectName);
      setProjectDescription(editingProject.projectDescription);
      setSelectedWorkflow(editingProject.workflow);
      setOwnerTransfer(editingProject.ownerTransfer);
      fetchCurrentCollaborators(editingProject.id);
    } else {
      setProjectName('');
      setProjectDescription('');
      setSelectedWorkflow(location.pathname.split('/')[1]);
      setOwnerTransfer('');
      setCurrentCollaborators([]);
    }
  }, [editingProject, location.pathname]);

  const handleProjectNameChange = (e) => {
    setProjectName(e.target.value);
    setNameError(null);
  };

  const handleProjectDescriptionChange = (e) => {
    setProjectDescription(e.target.value);
    setDescriptionError(null);
  };

  return (
    <AuthenticatedTemplate>
      <div className="fixed inset-0  flex justify-center items-center backdrop-filter backdrop-blur-sm">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden w-96 max-h-[90vh]">
          <div
            className="flex justify-between items-center p-4 border-b"
            style={{ backgroundColor: '#85AD4B' }}
          >
            <h3 className="text-lg font-semibold text-white">
              {editingProject ? 'Update Project' : 'Create Project'}
            </h3>
            <button
              className="text-xl text-white font-semibold"
              onClick={onClose}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="p-4 overflow-y-auto">
            {/* Project Name Input */}
            <div className="mb-4">
              <h6 className="py-1">
                Name your project <span className="text-red-500">*</span>
              </h6>
              <input
                className="w-full p-3 border rounded-xl"
                value={projectName}
                onChange={(e) => {
                  setProjectName(e.target.value);
                  setNameError(null);
                }}
                placeholder="Enter project name"
              />
              {nameError && <p className="text-red-500 text-sm">{nameError}</p>}
            </div>

            {/* Project Description Input */}
            <div className="p-1 overflow-y-auto max-h-[calc(90vh-80px)] scrollbar-thin scrollbar-thumb-[#85AD4B] scrollbar-track-transparent hover:scrollbar-thumb-[#6f8d30]">
              <h6 className="py-1">
                Project Description <span className="text-red-500">*</span>
              </h6>
              <textarea
                className="w-full p-2 border rounded-xl"
                value={projectDescription}
                onChange={(e) => {
                  setProjectDescription(e.target.value);
                  setDescriptionError(null);
                }}
                placeholder="Enter project description"
                rows={2}
              />
              {descriptionError && (
                <p className="text-red-500 text-sm">{descriptionError}</p>
              )}
            </div>

            {/* Collaborators Section */}
            <div className="mb-4">
              <h6 className="py-1">Add Collaborators</h6>
              <div className="relative">
                <button
                  type="button"
                  className="w-full p-3 border rounded-xl cursor-pointer flex justify-between items-center text-left"
                  onClick={() => setIsCollaboratorsOpen(!isCollaboratorsOpen)}
                >
                  <span className="text-gray-600">
                    {selectedCollaborators.length
                      ? `${selectedCollaborators.length} collaborator${
                          selectedCollaborators.length !== 1 ? 's' : ''
                        } selected`
                      : 'Select collaborators'}
                  </span>

                  <ChevronDown
                    className={`h-5 w-5 transform transition-transform ${
                      isCollaboratorsOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {/* Dropdown Content */}
                {isCollaboratorsOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-xl shadow-lg max-h-64 overflow-y-auto">
                    {/* Search Input */}
                    <div className="p-2 border-b sticky top-0 bg-white">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <input
                          type="text"
                          value={searchTerm}
                          onChange={(e) => searchCollaborators(e.target.value)}
                          placeholder="Search collaborators..."
                          className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm"
                        />
                      </div>
                    </div>

                    {/* Selected Collaborators */}
                    {selectedCollaborators.length > 0 && (
                      <div className="p-2 border-b">
                        <p className="text-xs text-gray-500 mb-2">
                          Selected Collaborators
                        </p>
                        {selectedCollaborators.map((collaborator) => (
                          <div
                            key={collaborator.id}
                            className="flex justify-between items-center p-2 hover:bg-gray-50 rounded"
                          >
                            <div>
                              <p className="text-sm font-medium">
                                {collaborator.firstName} {collaborator.lastName}
                              </p>
                              <p className="text-xs text-gray-600">
                                {collaborator.userEmail}
                              </p>
                            </div>
                            <button
                              onClick={() =>
                                handleRemoveCollaborator(collaborator)
                              }
                              className="text-red-500 hover:bg-red-50 p-1 rounded"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Search Results */}
                    {searchTerm ? (
                      <div className="p-2">
                        <p className="text-xs text-gray-500 mb-2">
                          Search Results
                        </p>
                        {loading ? (
                          <div className="flex justify-center items-center py-4">
                            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                          </div>
                        ) : collaborators.length > 0 ? (
                          collaborators.map((collaborator) => (
                            <div
                              key={collaborator.id}
                              className="flex justify-between items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                            >
                              <div>
                                <p className="text-sm font-medium">
                                  {collaborator.firstName}{' '}
                                  {collaborator.lastName}
                                </p>
                                <p className="text-xs text-gray-600">
                                  {collaborator.userEmail}
                                </p>
                              </div>
                              {/* Pass the entire collaborator object to handleAddCollaborator */}
                              <Plus
                                className="h-4 w-4 text-gray-400"
                                onClick={() =>
                                  handleAddCollaborator(collaborator)
                                }
                              />
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500 text-center py-2">
                            No results found
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="p-2">
                        <p className="text-xs text-gray-500 mb-2">
                          Current Collaborators
                        </p>
                        {loading ? (
                          <div className="flex justify-center items-center py-4">
                            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                          </div>
                        ) : currentCollaborators.length > 0 ? (
                          currentCollaborators.map((collab) => (
                            <div
                              key={collab.userId}
                              className="flex justify-between items-center p-2 hover:bg-gray-50 rounded"
                            >
                              <div>
                                <p className="text-sm font-medium">
                                  {collab.firstName} {collab.lastName}
                                </p>
                                <p className="text-xs text-gray-600">
                                  {collab.userEmail}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <span
                                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeStyle(collab.projectAccessRole)}`}
                                >
                                  {collab.projectAccessRole || 'No Role'}
                                </span>
                                {editingProject && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteCollaborator(collab.userId);
                                    }}
                                    className="text-red-500 hover:bg-red-50 p-1 rounded"
                                    disabled={deletingUserId === collab.userId}
                                  >
                                    {deletingUserId === collab.userId ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Trash2 className="h-4 w-4" />
                                    )}
                                  </button>
                                )}
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500 text-center py-2">
                            No collaborators yet
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {editingProject ? (
                  <div className="mt-4">
                    {/* <h6>Owner Transfer</h6>
                    <input
                      className="w-full p-2 border rounded mb-2"
                      value={ownerTransfer}
                      onChange={(e) => setOwnerTransfer(e.target.value)}
                      placeholder="Enter owner transfer details"
                    /> */}
                  </div>
                ) : (
                  <div className="mt-4">
                    <h6>Workflow</h6>
                    <select
                      className="w-full p-2 border rounded mb-2"
                      value={selectedWorkflow}
                      onChange={(e) => setSelectedWorkflow(e.target.value)}
                    >
                      <option value="">Select a workflow</option>
                      {availableWorkflows()}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Save Button */}
            <div className="mt-4 flex justify-end">
              <button
                onClick={handleSave}
                disabled={!isFormValid}
                className={`px-4 py-2 rounded-md w-[100px] ${
                  isFormValid
                    ? 'bg-transparent border text-black border-gray-600 hover:text-white hover:bg-[#6f8d30]'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed border border-gray-300'
                }`}
              >
                {editingProject ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </AuthenticatedTemplate>
  );
};

export default CreateProjectPopup;
